import { useMemo } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import type {
  ParkingSpotListResponse,
  PropertyGroupResponse,
  PropertyResponse,
} from '@/shared/api';
import type { ArrivalStep } from '@/features/guest/useGuestSession';
import { ParkingAlternativesBlock } from '@/features/guest/parking/ParkingAlternativesBlock';
import { ApartmentDescriptionBlock } from '@/features/guest/stay/ApartmentDescriptionBlock';
import { ArrivalBlock } from '@/features/guest/stay/ArrivalBlock';
import { EmergencyBlock } from '@/features/guest/stay/EmergencyBlock';
import { HouseRulesBlock } from '@/features/guest/stay/HouseRulesBlock';
import { AccessTiles } from '@/features/guest/travel/AccessTiles';
import { GuestCard, SectionHeader } from '@/features/guest/ui';
import { MapPin } from 'lucide-react';
import { guestRules, savedRules } from '../object-workspace/houseRulesModel';
import { ParkingPreviewLead } from '../object-workspace/ParkingPreviewLead';

// Wie der Server verdeckt: kein Code und kein Passwort wird in der Vorschau aufgelöst.
const MASK = '•••';

interface Props {
  group: PropertyGroupResponse;
  apartment?: PropertyResponse;
  steps: ArrivalStep[];
  alternatives: ArrivalStep[];
  parking?: ParkingSpotListResponse;
  parkingFailed: boolean;
  locale: 'de' | 'en';
}

/**
 * Der Anreisetag im Reisebegleiter aus den Bausteinen des Portals, in der Sprache des Gasts: der
 * Jetzt-Bereich mit verdeckten Codes und WLAN, darunter „Ankommen“.
 */
export function PortalStayPreview(props: Props) {
  const { i18n } = useTranslation();
  // Beide Sprachen fest: die App-Instanz folgt der Browsersprache des Betreibers, nicht der Wahl.
  const fixed = useMemo(
    () => ({ de: i18n.cloneInstance({ lng: 'de' }), en: i18n.cloneInstance({ lng: 'en' }) }),
    [i18n],
  );
  return (
    <I18nextProvider i18n={fixed[props.locale]}>
      <StayBlocks {...props} />
    </I18nextProvider>
  );
}

function StayBlocks({ group, apartment, steps, alternatives, parking, locale, ...props }: Props) {
  const { t } = useTranslation();
  // Wie das Portal: Englisch fällt auf Deutsch zurück, Deutsch nie auf Englisch.
  const description =
    locale === 'en'
      ? apartment?.apartmentDescriptionEn?.trim() || apartment?.apartmentDescriptionDe
      : apartment?.apartmentDescriptionDe;
  const rules = guestRules(savedRules(group), locale === 'en' ? 'En' : 'De').text;

  return (
    <div className="space-y-5">
      <GuestCard tone="forest" padding="none" className="guest-stripes relative overflow-hidden">
        <div className="relative space-y-4 p-4">
          <div className="space-y-1">
            <p className="text-forest-on-dark font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
              {t('travel.now.arrival.eyebrow')}
            </p>
            <h4 className="font-display text-2xl font-bold leading-tight text-white">
              {t('travel.now.arrival.title')}
            </h4>
            <p className="text-xs text-white/80">
              {t('travel.now.arrival.sub', {
                time: (group.checkInTime ?? '15:00').slice(0, 5),
                apartment: apartment?.name ?? group.name,
              })}
            </p>
          </div>
          <AccessTiles
            buildingCode={group.hasBuildingAccessCode ? MASK : null}
            keyCode={MASK}
            keyCodeAvailableFrom={null}
            wifiSsid={apartment?.wiFiSsid ?? null}
            wifiPassword={apartment?.hasWiFiPassword ? MASK : null}
          />
        </div>
      </GuestCard>
      <SectionHeader
        icon={<MapPin className="size-5" />}
        title={t('travel.arrival.title')}
        lede={t('travel.arrival.lede')}
      />
      <ApartmentDescriptionBlock description={description ?? null} />
      <div className="space-y-3">
        <ArrivalBlock steps={steps} token={undefined} />
        <ParkingPreviewLead parking={parking} failed={props.parkingFailed} locale={locale} />
        {alternatives.length > 0 && (
          // Der Block ragt wie im Portal 1,25 rem über seinen Rand; das Telefon hat nur 1 rem
          // Innenabstand und bekäme sonst eine Querscrollleiste.
          <div className="mx-1">
            <ParkingAlternativesBlock steps={alternatives} token={undefined} />
          </div>
        )}
        <HouseRulesBlock rules={rules.trim() ? rules : null} />
      </div>
      <EmergencyBlock contact={apartment?.emergencyContact ?? null} />
    </div>
  );
}
